import { createContext, useContext, useMemo, useState, useCallback, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const AdminContext = createContext(null);

const SESSION_KEY = "bd_admin_unlocked";

/**
 * AdminContext — unlock state for admin editor mode.
 * • PIN ("111") is verified server-side via POST /api/admin/unlock.
 * • Once unlocked, the flag is mirrored to sessionStorage so SPA refreshes
 *   and direct-URL navigation to /admin/* sub-pages don't snap back to
 *   the locked screen. Clearing the tab still re-locks the editor.
 */
export const AdminProvider = ({ children }) => {
  const { user } = useAuth();
  const [unlocked, setUnlocked] = useState(() => {
    try { return sessionStorage.getItem(SESSION_KEY) === "1"; } catch { return false; }
  });
  const [err, setErr] = useState("");

  useEffect(() => {
    try {
      if (unlocked) sessionStorage.setItem(SESSION_KEY, "1");
      else sessionStorage.removeItem(SESSION_KEY);
    } catch (e) {
      // sessionStorage blocked (private mode / cookies disabled). The
      // in-memory `unlocked` state still drives the UI for this tab —
      // we just won't survive a refresh.
      console.warn("[admin] sessionStorage unavailable; admin unlock won't survive refresh:", e?.message || e);
    }
  }, [unlocked]);

  const unlock = useCallback(async (pin) => {
    setErr("");
    try {
      const { data } = await api.post("/admin/unlock", { pin });
      if (data.is_admin) {
        setUnlocked(true);
        return true;
      }
      setErr("That code didn't unlock anything.");
      return false;
    } catch {
      setErr("Couldn't reach the server.");
      return false;
    }
  }, []);

  const lock = useCallback(() => setUnlocked(false), []);

  // As of Feb 2026, the platform's only admin is steveblast@gmail.com.
  // Add additional emails here if/when more admins come on board.
  const isAdminEmail = useMemo(
    () => !!user && ["steveblast@gmail.com"].includes((user.email || "").toLowerCase()),
    [user]
  );

  const value = useMemo(
    () => ({ unlocked, unlock, lock, err, isAdminEmail }),
    [unlocked, unlock, lock, err, isAdminEmail]
  );

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
};

export const useAdmin = () => useContext(AdminContext) || {
  unlocked: false, unlock: async () => false, lock: () => {}, err: "", isAdminEmail: false,
};
